/**
 * A copyable technical report for an unexpected error, so a user can paste something
 * support/devs can act on (and look up the exact backend event) instead of the bare
 * "Unknown server error" string.
 *
 * Every field is optional — network errors and gateway 502s have no structured response
 * body, and the client-side context is still worth reporting on its own.
 */
export interface ErrorReportContext {
  message?: string;
  /** App Insights operation id from the backend error body. */
  correlationId?: string;
  errorCode?: string | number;
  status?: number;
  method?: string;
  /** The failing request url. */
  endpoint?: string;
  sentryEventId?: string;
  user?: string;
  brand?: string;
  /** Where the user was in the app when it happened. */
  page?: string;
  appVersion?: string;
  stackTrace?: string;
}

const newReportId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());

export const buildErrorReport = (context: ErrorReportContext): string => {
  const request = [context.method?.toUpperCase(), context.endpoint, context.status && `(HTTP ${context.status})`]
    .filter(Boolean)
    .join(' ');

  const rows: [string, string | undefined][] = [
    ['Time (UTC)', new Date().toISOString()],
    ['User', context.user],
    ['Brand', context.brand],
    ['Page', context.page],
    ['Request', request || undefined],
    ['Error', context.message],
    ['Error code', context.errorCode != null ? String(context.errorCode) : undefined],
    ['Correlation ID', context.correlationId],
    ['Sentry event', context.sentryEventId],
    ['App release', context.appVersion],
    ['Report ID', newReportId()],
  ];

  return [
    'Resourced error report',
    ...rows.filter(([, value]) => !!value).map(([label, value]) => `${label}: ${value}`),
    ...(context.stackTrace ? ['', context.stackTrace] : []),
  ].join('\n');
};
